import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Text, {
  TEXT_COLOR,
  TEXT_SIZE,
  TEXT_KIND,
} from '@/bit/components/components.text';
import { sendTaggingInfo } from './utils';
import { maskSeparation as applyMaskSeparation } from './utils';
import { calculateNewCaret } from './utils';

export const COMPONENT_NAME = 'input-form';
export const COMPONENT_THEME = {
  BOX: 'input-form--default',
  NO_BOX: 'input-form--no-box',
};
function Input({
  className,
  id,
  name,
  label,
  theme,
  mask,
  info,
  error,
  value,
  onFocus,
  onChange,
  onBlur,
  autoComplete,
  loaded,
  showError,
  readOnly,
  sendTagging,
  type,
}) {
  const [inputValue, setInputValue] = useState('');
  const [key, setKey] = useState('');
  const taggingInfo = {
    componentVariation: 'all',
    componentProduct: '',
    componentName: 'input',
    componentContent: label,
  };

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleOnFocus = () => {
    if(sendTagging){
      sendTaggingInfo(taggingInfo, 'focus');
    }
    onFocus();
  };

  const inputOnKeyDown = (e) => {
    setKey(e.key);
  };
  const handleOnChange = (event) => {
    const currentValue = event.target.value;
    let maskedValue = currentValue;
    if (mask) {
      const { target } = event;
      let newCaretPosition = target.selectionStart;
      const { maxLength, separationLength, separator } = mask;
      maskedValue = applyMaskSeparation(
        currentValue,
        maxLength,
        separationLength,
        separator
      );
      newCaretPosition = calculateNewCaret(
        key,
        newCaretPosition,
        separationLength
      );
      target.value = maskedValue;
      target.setSelectionRange(newCaretPosition, newCaretPosition);
    }
    setInputValue(maskedValue);
    onChange(event);
  };

  const handleOnBlur = (event) => {
    onBlur(event);
  };
  const labelClassName = error
    ? 'input__label input__label--error'
    : 'input__label';
  return (
    <div className={`input ${className}`} data-cy={id}>
      <div className={`skeleton skeleton-input ${loaded ? 'loaded' : ''}`} />
      <div className={theme}>
        <input
          className="input__field"
          id={id}
          data-cy={`${id}__input`}
          name={name}
          value={inputValue}
          label={label}
          type={type}
          placeholder=" "
          onChange={handleOnChange}
          onBlur={handleOnBlur}
          onKeyDown={inputOnKeyDown}
          onFocus={handleOnFocus}
          autoComplete={autoComplete}
          readOnly={readOnly}
        />
        <label htmlFor={id} className={labelClassName} data-cy={`${id}__label`}>
          {label}
        </label>
      </div>
      {showError && loaded && error && (
        <Text
          className="input__error"
          text={error}
          color={TEXT_COLOR.RED}
          kind={TEXT_KIND.SPAN}
          size={TEXT_SIZE.CAPTION3}
          cyData={`${id}__error`}
        />
      )}
      {loaded && info && !error && (
        <Text
          className="input__error line-height-120"
          text={info}
          color={TEXT_COLOR.GREY5}
          kind={TEXT_KIND.SPAN}
          size={TEXT_SIZE.CAPTION3}
          cyData={`${id}__info`}
        />
      )}
      <style jsx>{`@import "styles/colors.scss";
  .input {
    position: relative;
  }
  .input-form--default {
    background-color: $color--white;
    border: 0.0625rem solid $color--grey_300;
    border-radius: 0.25rem;
    height: 3.5rem;
    width: 100%;
    position: relative;
  }
  .input-form--default {
    background-color: $color--white;
    border-radius: 0.25rem;
    height: 3.5rem;
    width: 100%;
    position: relative;
  }
  .skeleton-input {
    position: absolute;
    border-radius: 0.25rem;
    top: 0;
    left: 0;
  }
  .input__field {
    border: 0;
    outline: none !important;
    font-size: 1rem;
    font-family: Regular;
    color: $color--movistar_black;
    padding: 1.5rem 1.25rem 0.5rem 0.75rem;
    background: transparent;
    width: 100%;
    border-radius: 0.25rem;
    cursor: pointer;
  }

  .input__field::placeholder {
    color: transparent;
  }
  .input__label {
    position: absolute;
    font-size: 1rem;
    font-family: Regular;
    cursor: text;
    pointer-events: none;
    white-space: nowrap;
    color: $color--grey_600;
    width: 90%;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .input__field:placeholder-shown ~ .input__label {
    top: 1.0625rem;
    left: 0.75rem;
  }
  .input__field:not(:placeholder-shown) ~ .input__label {
    top: 0.5625rem;
    left: 0.75rem;
    font-size: 0.75rem;
    &--error {
      color: $color--red_high;
    }
  }
  .input__field:focus ~ .input__label {
    top: 0.5625rem;
    left: 0.75rem;
    font-size: 0.75rem;
    color: $color--movistar_blue;
    &--error {
      color: $color--red_high;
    }
  }
  .input__field:focus {
    caret-color: $color--movistar_blue;
  }
  :global(.input__error) {
    padding: 0.25rem 0 0 0.75rem;
  }
  /* remove styles from autocomplete */
  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus {
    -webkit-text-fill-color: $color--movistar_black !important;
    -webkit-box-shadow: 0 0 0 62.5rem #ffffff inset;
  }
  /* remove styles from type number */
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  input[type='number'] {
    -moz-appearance: textfield;
  }

  @media screen and (min-width: 768px) {
    .input-form--default {
      height: 3rem;
    }
    .input-form--no-box {
      height: 3rem;
    }
    .input__field {
      padding: 1.25rem 1.25rem 0.25rem 0.75rem;
    }
    .input__field:placeholder-shown ~ .input__label {
      top: 0.875rem;
    }
    .input__field:not(:placeholder-shown) ~ .input__label {
      top: 0.25rem;
    }
    .input__field:focus ~ .input__label {
      top: 0.25rem;
    }
    label,
    .input__field:focus ~ .input__label {
      top: 0.25rem;
      left: 0.75rem;
    }
  }
`}</style>
      </div>
  );
}
Input.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  theme: PropTypes.string,
  className: PropTypes.string,
  label: PropTypes.string,
  onFocus: PropTypes.func,
  mask: PropTypes.object,
  error: PropTypes.string,
  info: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  autoComplete: PropTypes.string,
  value: PropTypes.string,
  loaded: PropTypes.bool,
  showError: PropTypes.bool,
  readOnly: PropTypes.bool,
  sendTagging: PropTypes.bool,
  type: PropTypes.string,
};

Input.defaultProps = {
  className: '',
  label: 'input',
  theme: COMPONENT_THEME.BOX,
  error: '',
  info: '',
  mask: null,
  onChange: () => {},
  onBlur: () => {},
  onFocus: () => {},
  autoComplete: 'on',
  value: '',
  loaded: true,
  showError: true,
  readOnly: false,
  sendTagging: false,
  type: 'text',
};
export default Input;
